import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type StorageServicesPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createStorageServicesPage(config: StorageServicesPageConfig): ComponentType {
  return function StorageServicesPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/storage-solutions"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Clean, dry, and secure storage facilities serving ${location}`,
  'Short-term and long-term storage options with flexible contracts',
  'Optional collection, packing, and re-delivery by the VIP House Movers team',
  '24/7 monitored premises with alarm, CCTV, and controlled access',
];

const baseProcess = (location: string): string[] => [
  `We discuss your storage needs in ${location} and recommend the right unit size and duration.`,
  'Our team can collect, wrap, and transport your items straight into storage for you.',
  'Your belongings are carefully logged, labelled, and placed into your secure storage unit.',
  'When you are ready, we deliver everything back to your new address on a date that suits you.',
];

const baseIdealFor = (location: string): string[] => [
  `Homeowners and tenants in ${location} with a gap between moving dates`,
  'Businesses needing secure storage for documents, stock, or office furniture',
  'Anyone downsizing, renovating, or travelling who wants their belongings kept safe',
];

export const storageServicesPageConfigs: StorageServicesPageConfig[] = [
  {
    path: '/services/storage-solutions/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Storage Services in Staffordshire',
    title: 'Storage Services in Staffordshire',
    description: 'Secure, flexible storage services across Staffordshire from VIP House Movers, ideal for home moves, businesses, and long-term storage needs.',
    intro: 'Whether you are between homes, decluttering, or need extra space for your business, our Staffordshire storage service keeps your belongings safe and easy to access whenever you need them.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/storage-solutions/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Storage Services in Wolverhampton',
    title: 'Storage Services in Wolverhampton',
    description: 'VIP House Movers offers premium storage services in Wolverhampton with secure facilities, flexible terms, and full moving support.',
    intro: 'As a Wolverhampton-based removals company with its own storage facility, we make it simple to combine your move and storage into one smooth, professional service.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/storage-solutions/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Storage Services in Birmingham',
    title: 'Storage Services in Birmingham',
    description: 'Reliable storage services in Birmingham by VIP House Movers, providing secure units and a joined-up move-and-store experience.',
    intro: 'From flat moves to large family homes and offices across Birmingham, our storage service gives you a safe, convenient place to keep your belongings for as long as you need.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/storage-solutions/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Storage Services in Walsall',
    title: 'Storage Services in Walsall',
    description: 'Secure, affordable storage services in Walsall from VIP House Movers, ideal for home moves, renovations, and business needs.',
    intro: 'Our Walsall storage service is designed around real life. Flexible durations, clear pricing, and full moving support mean your items are looked after from the moment they leave your door.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/storage-solutions/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Storage Services in Cannock',
    title: 'Storage Services in Cannock',
    description: 'Premium storage services in Cannock by VIP House Movers, combining secure facilities with professional collection and delivery.',
    intro: 'Whether you need short-term storage during a Cannock house move or a longer solution for business stock and furniture, our team takes care of the handling, transport, and security for you.',
    highlights: baseHighlights('Cannock'),
    process: baseProcess('Cannock'),
    idealFor: baseIdealFor('Cannock'),
  },
];

export const storageServicesPages = storageServicesPageConfigs.map((config) => ({
  ...config,
  component: createStorageServicesPage(config),
}));
